import { classNames } from "primereact/utils";
import styles from "./HandoverNoteDialog.module.scss";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DismissibleNote } from "../../../../common/components/common/DismissibleNote/DismissibleNote";
import { ColorButton } from "../../../../common/components/button/colorButton/ColorButton";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { IHandoverEntryModel } from "../models/handoverNoteModel";
import { HANDOVER_MAX_CONTENT_LENGTH } from "../hooks/useHandoverNote";

interface IHandoverNoteDialogProps {
  visible: boolean;
  selectedDate: Date;
  entries: IHandoverEntryModel[];
  draft: string;
  hasDraft: boolean;
  canSubmit: boolean;
  isSaving: boolean;
  onDraftChange: (value: string) => void;
  onSubmit: () => void;
  onComposerKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onDelete: (entryId: number) => void;
  onHide: () => void;
}

/**
 * The day's hand-over thread in full: every message with its author and time, plus the
 * box for adding the next one. Opening this is what marks the day read.
 */
export const HandoverNoteDialog = ({
  visible,
  selectedDate,
  entries,
  draft,
  hasDraft,
  canSubmit,
  isSaving,
  onDraftChange,
  onSubmit,
  onComposerKeyDown,
  onDelete,
  onHide,
}: IHandoverNoteDialogProps) => {
  return (
    <AppDialog
      header={`Бележки за ${dateUtility.formatDate(selectedDate)}`}
      headerIconType={IconType.Notes}
      headerClassName={styles.root_header}
      contentClassName={styles.root_content}
      // PrimeReact sizes a dialog to its contents, so an empty day rendered wider
      // than a day holding one short note. Pin the width instead of letting the
      // thread decide it.
      style={{ width: "min(560px, 92vw)" }}
      visible={visible}
      onHide={onHide}
      dismissableMask={false}
      // Closing clears the box, so both quick ways out are blocked while there is
      // something to lose. With an empty box Escape stays the normal way out.
      closeOnEscape={!hasDraft}
    >
      <div className={styles.root}>
        {/* Nothing about a thread of messages says what it is FOR, so somebody opening
            this for the first time gets told. It is remembered per browser once
            closed, so it costs a returning user a single click, once. */}
        <DismissibleNote
          storageKey="plannerHandoverNotesIntro"
          title="За какво служат бележките"
          text={
            <>
              Тук екипът си оставя съобщения за деня: какво се е случило на
              смяната, какво трябва да се довърши и за какво да внимава
              следващият. Целият екип ги вижда и всеки може да допише.
              <span className={styles.root_intro_example}>
                Например: „Зала 1 трябва да се почисти преди 18:00" или „Клиент
                забрави яке на рецепцията".
              </span>
            </>
          }
        />

        <RenderWhen condition={entries.length === 0}>
          <p className={styles.root_empty}>Още няма бележки за този ден.</p>
        </RenderWhen>

        <RenderWhen condition={entries.length > 0}>
          <div className={styles.root_thread}>
            {entries.map((entry) => (
              <div
                key={entry.id}
                className={classNames(styles.root_entry, {
                  [styles.root_entry_unread]: entry.isUnread,
                })}
              >
                <div className={styles.root_entry_meta}>
                  <span className={styles.root_entry_author}>
                    {entry.createdBy}
                  </span>
                  <span className={styles.root_entry_time}>
                    {dateUtility.formatDateTime(entry.createdOn)}
                  </span>
                  <RenderWhen condition={entry.canDelete}>
                    <button
                      type="button"
                      className={styles.root_entry_delete}
                      onClick={() => onDelete(entry.id)}
                      aria-label="Изтрий бележката"
                    >
                      <Icon
                        type={IconType.DeleteIcon}
                        width="14px"
                        height="14px"
                      />
                    </button>
                  </RenderWhen>
                </div>
                <p className={styles.root_entry_content}>{entry.content}</p>
              </div>
            ))}
          </div>
        </RenderWhen>

        <div className={styles.root_composer}>
          <textarea
            className={styles.root_composer_input}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onComposerKeyDown}
            placeholder="Напишете бележка за екипа..."
            maxLength={HANDOVER_MAX_CONTENT_LENGTH}
            rows={3}
          />
          <ColorButton
            className={styles.root_composer_submit}
            label="Добави"
            backgroundColorHex="var(--accent-from, #7877c6)"
            textColorHex="var(--accent-text, #ffffff)"
            onClick={onSubmit}
            loading={isSaving}
            disabled={!canSubmit}
          />
        </div>
      </div>
    </AppDialog>
  );
};
